/**
 * Admin bulk archive: "archive resolved conversations older than N days"
 * with a live dry-run count before committing. Self-contained result
 * display — no toast dependency.
 */
import { useEffect, useRef, useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, Button, Input, Spinner,
} from '@/components/ui'

interface BulkArchiveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** dry_run=true call; resolves to the would-be count, null on failure. */
  onDryRun: (days: number) => Promise<number | null>
  /** Real run; resolves to the archived count, null on failure. */
  onConfirm: (days: number) => Promise<number | null>
}

export function BulkArchiveDialog({ open, onOpenChange, onDryRun, onConfirm }: BulkArchiveDialogProps) {
  const [days, setDays] = useState(30)
  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [resultCount, setResultCount] = useState<number | null>(null)
  const [isWorking, setIsWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Latest-callback ref (same idiom as useInboxSSE's onEventRef): the parent
  // passes an inline arrow whose identity changes every render, and SSE-driven
  // silent refreshes re-render the page frequently — keeping the callback out
  // of the effect deps stops those re-renders from resetting the debounce.
  const onDryRunRef = useRef(onDryRun)
  onDryRunRef.current = onDryRun

  // Monotonic sequence so a stale (out-of-order) dry-run resolution can't
  // overwrite a newer preview.
  const requestSeq = useRef(0)

  // Debounced dry-run preview whenever the dialog is open and days changes.
  useEffect(() => {
    if (!open) {
      // Invalidate any in-flight dry-run so its resolution can't set
      // stale error/preview state that would be visible on reopen.
      requestSeq.current++
      setResultCount(null)
      setError(null)
      setDays(30)
      return
    }
    if (resultCount !== null) return
    setPreviewCount(null)
    const timer = setTimeout(async () => {
      const seq = ++requestSeq.current
      const count = await onDryRunRef.current(days)
      if (seq !== requestSeq.current) return
      setPreviewCount(count)
      if (count === null) {
        setError('Could not compute the preview — check the connection and retry.')
      } else {
        // A later successful preview must clear a prior preview error.
        setError(null)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [open, days, resultCount])

  const handleConfirm = async () => {
    setIsWorking(true)
    setError(null)
    const count = await onConfirm(days)
    setIsWorking(false)
    if (count === null) {
      setError('Bulk archive failed — please retry.')
    } else {
      setResultCount(count)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk archive resolved conversations</DialogTitle>
          <DialogDescription>
            Archives resolved conversations with no activity for the chosen
            period. Archived conversations move to the Archived tab and can
            be unarchived individually.
          </DialogDescription>
        </DialogHeader>

        {resultCount !== null ? (
          <p className="text-sm">Archived {resultCount} conversations.</p>
        ) : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              Older than
              <Input
                type="number"
                min={1}
                value={days}
                onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
                className="w-20 h-8 text-sm"
              />
              days
            </label>
            <p className="text-sm text-muted-foreground min-h-5">
              {previewCount === null && !error && (
                <span className="inline-flex items-center gap-2"><Spinner size="sm" /> Counting…</span>
              )}
              {previewCount !== null && `This will archive ${previewCount} conversations.`}
            </p>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          {resultCount !== null ? (
            <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleConfirm}
                disabled={isWorking || previewCount === null || previewCount === 0}
              >
                {isWorking ? 'Archiving…' : 'Archive conversations'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
